import React, { useEffect, useState } from 'react';
import { ConfirmDialogUI, DataGridIdentifiers, DatagridPremiumUi } from '../../Interface';
import { dataGridPremiumColumns } from '../../../utils/helper';
import settlementGeneratorService from '../../../service/attendanceSettlement/settlementGenerator.service';
import { Box, Button } from '@mui/material';
import { useLoader } from '../../../provider/LoaderProvider';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import { formatCurrency } from '../../../utils/currencyHelper';
import useParam from '../../../context/ParamContext';

const AttendanceTable = ({ checkedProjectState, filter, autoCompleteOptions, attendanceFilter, buildFilter }) => {
  const { params } = useParam();
  const { showLoader, hideLoader } = useLoader();
  const { checkedProjectIds } = checkedProjectState;
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [refetchTable, setRefetchTable] = useState(false);
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [],
    },
    pagination: {
      paginationModel: {
        pageSize: -1,
        page: 0,
      },
    },
    density: 'compact',
  };

  useEffect(() => {
    if (checkedProjectIds.length > 0) {
      setRefetchTable(true);
    } else {
      setReports({ rows: [], rowCount: 0 });
      setSelectedRowIds([]);
    }
  }, [checkedProjectIds, attendanceFilter]);

  const notSettledStatusParam = params.ATTENDANCE_NOT_SETTLED_STATUS_ID;
  const notSettledStatusId = Boolean(notSettledStatusParam) ? parseInt(notSettledStatusParam) : null;

  const getList = (gridState) => {
    if (checkedProjectIds.length === 0) {
      setRefetchTable(false);
      setReports({ rows: [], rowCount: 0 });
      return;
    }

    const filterItems = [
      { field: 'projectId', value: checkedProjectIds, operator: 'isAnyOf' },
      { field: 'startDate', value: filter.startDate, operator: 'equals' },
      { field: 'endDate', value: filter.endDate, operator: 'equals' },
    ];

    if (attendanceFilter.statusId) {
      filterItems.push({
        field: 'statusId',
        value: attendanceFilter.statusId,
        operator: 'equals',
      });
    }

    const finalFilter = buildFilter(gridState, filterItems);

    settlementGeneratorService.getAttendanceUsers(finalFilter).then((data) => {
      setReports(data);
      setRefetchTable(false);

      const defSelected = data.rows
        .filter((row) => row.attendance.statusId === notSettledStatusId)
        .map((row) => row.listId);
      setSelectedRowIds(defSelected);
    });
  };

  const handleGenClick = () => {
    setShowConfirmDialog(false);
    showLoader();

    addNotification({
      content: 'Elszámolások készítése elkezdődött!',
      type: NotificationType.INFO,
    });

    const settlementBody = checkedProjectIds
      .map((projectId) => {
        const rowsForProject = reports.rows.filter(
          (row) => selectedRowIds.includes(row.listId) && row.attendance.projectId === projectId
        );

        if (rowsForProject.length === 0) return null;

        return {
          companyId: filter.companyId,
          projectId: projectId,
          attendanceSettlementLine: rowsForProject.map((row) => ({
            userId: row.userId,
            attendanceDate: row.attendance.date,
            timeSpent: row.timeSpent,
            baseId: row.attendance.id,
            baseLine: row.listId,
            price: row?.price?.price,
            currencyCode: row?.price?.currencyCode,
            itemCode: row?.role?.itemCode,
          })),
        };
      })
      .filter(Boolean);

    settlementGeneratorService
      .createAttendanceSettlement(settlementBody)
      .then(() => {
        addNotification({
          content: 'Elszámolások készítése sikeres!',
          type: NotificationType.SUCCESS,
        });
        setRefetchTable(true);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const columns = dataGridPremiumColumns([
    {
      field: 'date',
      headerName: 'Dátum',
      type: 'date',
      sortable: false,
      filterable: false,
      valueGetter: (_data, row) => {
        return new Date(row.attendance.date);
      },
    },
    {
      field: 'userId',
      headerName: 'Dolgozó',
      type: 'string',
      sortable: false,
      filterable: false,
      renderCell: (data) => {
        return autoCompleteOptions.resourceOne.find((r1) => r1.value === data.row.userId)?.label;
      },
    },
    {
      field: 'timeSpent',
      headerName: 'Idő (óra)',
      sortable: false,
      filterable: false,
      type: 'number',
    },
    {
      field: 'statusId',
      headerName: 'Státusz',
      type: 'string',
      sortable: false,
      filterable: false,
      renderCell: (data) => {
        return data.row.attendance.status.itemName;
      },
    },
    {
      field: 'projectId',
      headerName: 'Projekt',
      type: 'string',
      sortable: false,
      filterable: false,
      renderCell: (data) => {
        return data.row.attendance.project.projectName;
      },
    },
    {
      field: 'price',
      headerName: 'Óradíj',
      type: 'number',
      sortable: false,
      filterable: false,
      renderCell: (data) => {
        return data.row?.price?.price ? formatCurrency(data.row?.price?.price) : null;
      },
    },
    {
      field: 'currencyCode',
      headerName: 'Pénznem',
      type: 'string',
      sortable: false,
      filterable: false,
      renderCell: (data) => {
        return data.row?.price?.currencyCode;
      },
    },
    {
      field: 'role',
      headerName: 'Szerep',
      type: 'string',
      sortable: false,
      filterable: false,
      renderCell: (data) => {
        return data.row?.role?.itemCode;
      },
    },
  ]);

  return (
    <>
      <DatagridPremiumUi
        datagridIdentifier={DataGridIdentifiers.AttendanceSettlementTable}
        datagridInitialState={initialState}
        rows={reports.rows}
        refetch={{ refetchTable, setRefetchTable }}
        dataLoader={getList}
        rowCount={reports.rowCount}
        getRowId={(row) => row.listId}
        columns={columns}
        paginationAndfilterMode="server"
        height="400px"
        gridExport={true}
        headerFilters={false}
        rowSelectionModel={selectedRowIds}
        onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
        checkboxSelection
        isRowSelectable={(params) => params.row.attendance.statusId === notSettledStatusId}
        disableRowSelectionOnClick
        customHeaderComponent={
          <Box ml="auto">
            <Button
              variant="text"
              size="small"
              color="primary"
              disableElevation
              disabled={selectedRowIds.length === 0}
              onClick={() => setShowConfirmDialog(true)}
            >
              {`Elszámolás készítése (${selectedRowIds.length})`}
            </Button>
          </Box>
        }
      />
      <ConfirmDialogUI
        handleChangeElement={() => {
          handleGenClick();
        }}
        show={showConfirmDialog}
        onHide={() => {
          setShowConfirmDialog(false);
        }}
        message={`Elkészíti a(z) ${selectedRowIds.length} elszámolást?`}
      />
    </>
  );
};

export default AttendanceTable;
